import classNames from 'classnames';
import { memo, type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { type PublicClip } from '@air/api';
import { type BaseCustomField, type Clip, ClipType } from '@air/api/types';
import { Comment, Stack } from '@air/next-icons';
import { Spinner } from '@air/primitive-spinner';
import { ClipUtils } from '@air/utils-clips';

import { CardAction } from '~/components/CardAction';
import { AssetGalleryCardMetaDetails } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardMetaDetails';
import { AssetGalleryCardRightActions } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardRightActions';
import { AssetGalleryCardShortInfo } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardShortInfo';
import { AssetGalleryCardThumbnail } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardThumbnail';
import { CustomFieldsDisplay } from '~/components/Gallery/CustomFieldsDisplay';
import { GalleryCard, type GalleryCardProps } from '~/components/Gallery/GalleryCard/GalleryCard';
import { GalleryCardCustomFieldsAction } from '~/components/Gallery/GalleryCardCustomFieldsAction';
import { RiveGalleryCard } from '~/components/Gallery/RiveGalleryCard/RiveGalleryCard';
import { GalleryItemType } from '~/components/Gallery/types';
import { getAssetCustomFields } from '~/components/Gallery/utils';
import { VideoScrubbableThumbnail } from '~/components/VideoScrubbableThumbnail';
import { VideoStaticThumbnail } from '~/components/VideoStaticThumbnail';
import { GallerySmartSearchRelevanceIndicator } from '~/components/v2/components/shared/gallery/GallerySmartSearchRelevanceIndicator';
import { ASSET_CARD, ASSET_CARD_TITLE } from '~/constants/testIDs';
import { useVideoScrubbableThumbnail } from '~/hooks/useVideoScrubbableThumbnail';
import {
  isTitleAndMetadataVisibleSelector,
  visibleCustomFieldsViewOptionsSelector,
} from '~/store/configViews/selectors';
import { type SelectableGalleryAssetItem } from '~/store/selectedItems/types';
import { getCanvasImportPreview } from '~/utils/canvasImportPreviewCache';
import { useAirSelector } from '~/utils/ReduxUtils';
import { isRiveFile } from '~/utils/RiveUtils';
import { clipSupportsStaticThumbnail } from '~/utils/videoThumbnails';

const CARD_INNER_PADDING = 16;
const CARD_INNER_PADDING_METADATA = 4;

export type AssetGalleryCardProps<C> = {
  cardHeight: number;
  cardWidth: number;
  clip: C;
  onAssetClick: () => void;
  onAssetTitleCmdClick?: () => void;
  onCustomFieldsClick: () => void;
  onDiscussionsClick?: () => void;
  onVersionsClick?: () => void;
  showVersionsIndicator: boolean;
  canViewCustomFields: boolean;
  allCustomFields: BaseCustomField[] | undefined;
  shouldShowDiscussions: boolean;
} & Pick<
  GalleryCardProps<SelectableGalleryAssetItem>,
  'getButtonClassName' | 'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable'
>;

const _AssetGalleryCard = <C extends Clip | PublicClip>({
  allCustomFields = [],
  cardHeight,
  cardWidth,
  clip,
  getItemMenuOptions,
  isSelectable = true,
  canViewCustomFields,
  onAssetClick,
  onAssetTitleCmdClick,
  getSelectionMenuOptions,
  onCustomFieldsClick,
  onDiscussionsClick,
  onVersionsClick,
  showVersionsIndicator,
  shouldShowDiscussions,
  getButtonClassName,
}: AssetGalleryCardProps<C>) => {
  const clipDimensions = useMemo(() => {
    const aspectRatio = clip.width / clip.height;

    return {
      width: cardWidth,
      height: cardWidth / aspectRatio,
    };
  }, [cardWidth, clip.height, clip.width]);

  const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } =
    useVideoScrubbableThumbnail({
      spritesheet: clip.assets.spriteSheet,
      containerDimensions: {
        height: cardHeight,
        width: cardWidth,
      },
    });
  const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);

  const [hasCanvasImportPreview, setHasCanvasImportPreview] = useState(
    () => !!getCanvasImportPreview(clip.importedName),
  );

  const handlePreviewReleased = useCallback(() => {
    setHasCanvasImportPreview(false);
  }, []);

  useEffect(() => {
    if (clip.assets.spriteSheet?.src) {
      handlePreviewReleased();
    }
  }, [clip.assets.spriteSheet?.src, handlePreviewReleased]);

  const customFields = useMemo(
    () => getAssetCustomFields({ allCustomFields, clip, visibleCustomFields }),
    [allCustomFields, clip, visibleCustomFields],
  );

  const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;
  const titleLabel = clip.title || clip.importedName || 'Untitled';

  const popoverCustomFields = useMemo(
    () =>
      clip?.customFields?.filter((customField) => {
        return allCustomFields.some((cf) => cf.id === customField.id);
      }),
    [clip, allCustomFields],
  );

  // If this is a Rive file, use the specialized RiveGalleryCard
  if (isRiveFile(clip)) {
    return (
      <RiveGalleryCard
        file={clip}
        cardHeight={cardHeight}
        cardWidth={cardWidth}
        getItemMenuOptions={getItemMenuOptions}
        isSelectable={isSelectable}
        getSelectionMenuOptions={getSelectionMenuOptions}
        onFileClick={onAssetClick}
        onFileTitleCmdClick={onAssetTitleCmdClick}
        onCustomFieldsClick={onCustomFieldsClick}
        onDiscussionsClick={onDiscussionsClick}
        onVersionsClick={onVersionsClick}
        showVersionsIndicator={showVersionsIndicator}
        canViewCustomFields={canViewCustomFields}
        allCustomFields={allCustomFields}
        getButtonClassName={getButtonClassName}
      />
    );
  }

  return (
    <GalleryCard
      testId={ASSET_CARD}
      title={titleLabel}
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      item={{
        id: clip.id,
        item: clip,
        type: GalleryItemType.asset,
      }}
      onClick={onAssetClick}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      isSelectable={isSelectable}
      getButtonClassName={getButtonClassName}
      data-clip-state={clip.status}
    >
      {({ setIsMenuOpen, isHovering, isSelected, isHighlighted, isMenuOpen }) => {
        let thumbnail: ReactElement;

        if (clip.assets.spriteSheet?.src && clip.type === ClipType.video && isHovering) {
          thumbnail = (
            <VideoScrubbableThumbnail
              backgroundImageRef={backgroundImageRef}
              indicatorRef={indicatorRef}
              src={clip.assets.spriteSheet.src}
              style={backgroundImageStyles}
            />
          );
        } else if (clipSupportsStaticThumbnail(clip)) {
          thumbnail = (
            <VideoStaticThumbnail
              clip={clip}
              containerDimensions={{
                height: cardHeight,
                width: cardWidth,
              }}
            />
          );
        } else {
          thumbnail = (
            <AssetGalleryCardThumbnail
              clip={clip}
              isHovering={isHovering}
              isSelected={isSelected || isHighlighted}
              onPreviewReleased={handlePreviewReleased}
              width={clipDimensions.width}
              height={clipDimensions.height}
            />
          );
        }

        return (
          <>
            <div className="pointer-events-none relative grow">
              {thumbnail}
              {(ClipUtils.isProcessing(clip.status) || hasCanvasImportPreview) && !clip.assets.spriteSheet?.src && (
                <div className="shimmer-image-overlay absolute inset-0 rounded-4" />
              )}
              {!isTitleAndMetadataVisible && (
                <div
                  className={classNames(
                    'absolute right-0 bottom-0 left-0 flex h-[100px] flex-col justify-end rounded-4 rounded-t-none bg-gradient-to-b from-black/0 to-black/90 px-2 pt-1 pb-2',
                    isHovering ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  <AssetGalleryCardMetaDetails
                    hasDarkBackground
                    clip={clip}
                    title={titleLabel}
                    onTitleClick={onAssetClick}
                    onTitleCmdClick={onAssetTitleCmdClick}
                    /**
                     * The width is equal to the card width minus the padding on the left and right
                     */
                    width={cardWidth - CARD_INNER_PADDING}
                    titleTestId={ASSET_CARD_TITLE}
                  />
                </div>
              )}
              {(isTitleAndMetadataVisible || (!isHovering && !isMenuOpen)) && (
                <div className="absolute right-2 bottom-2 z-10">
                  <AssetGalleryCardShortInfo clip={clip} />
                </div>
              )}

              <div className="pointer-events-none absolute top-2 left-2 flex gap-1">
                {(ClipUtils.isProcessing(clip.status) || hasCanvasImportPreview) && !clip.assets.spriteSheet?.src && (
                  <div className="flex items-center gap-1 rounded-4 bg-black/60 p-1 backdrop-blur-sm">
                    <Spinner className="size-4 text-white" />
                    <span className="font-medium text-10 text-white">Processing…</span>
                  </div>
                )}
                <GallerySmartSearchRelevanceIndicator clip={clip} />
                {!!popoverCustomFields?.length && canViewCustomFields && (
                  <GalleryCardCustomFieldsAction customFields={popoverCustomFields} onClick={onCustomFieldsClick} />
                )}
                {showVersionsIndicator && !!onVersionsClick && (
                  <CardAction
                    data-testid="CARD_VERSIONS_INDICATOR"
                    icon={<Stack className="size-4" />}
                    onClick={onVersionsClick}
                  />
                )}
                {shouldShowDiscussions && !!onDiscussionsClick && !!clip.openCommentCount && (
                  <CardAction
                    data-testid="CARD_DISCUSSIONS_INDICATOR"
                    icon={<Comment className="size-4" />}
                    onClick={onDiscussionsClick}
                    text={clip.openCommentCount}
                  />
                )}
              </div>
              {isHovering && (
                <div className="pointer-events-none absolute top-2 right-2 flex gap-1">
                  <AssetGalleryCardRightActions
                    clip={clip}
                    getItemMenuOptions={getItemMenuOptions}
                    getSelectionMenuOptions={getSelectionMenuOptions}
                    setIsMenuOpen={setIsMenuOpen}
                  />
                </div>
              )}
            </div>

            {shouldShowCustomFields || isTitleAndMetadataVisible ? (
              <div className="pointer-events-none relative mx-0.5 mt-1.5 flex flex-col items-start gap-1">
                {isTitleAndMetadataVisible && (
                  <AssetGalleryCardMetaDetails
                    clip={clip}
                    title={titleLabel}
                    onTitleClick={onAssetClick}
                    /** Card width minus the left and right padding */
                    width={cardWidth - CARD_INNER_PADDING_METADATA}
                    titleTestId={ASSET_CARD_TITLE}
                  />
                )}
                {shouldShowCustomFields && (
                  <CustomFieldsDisplay
                    className={classNames('px-1', isTitleAndMetadataVisible ? 'mt-0.5' : undefined)}
                    customFields={customFields}
                    onClick={onCustomFieldsClick}
                  />
                )}
              </div>
            ) : null}
          </>
        );
      }}
    </GalleryCard>
  );
};

export const AssetGalleryCard = memo(_AssetGalleryCard) as typeof _AssetGalleryCard;
